import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, IconButton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CommonDataGrid from "../../../components/datagrid/CommonDataGrid";
import { useDispatch } from "react-redux";
import {
  deleteSell,
  getSellsWithPagination,
} from "../../../redux/sell/sellSlice";
import { Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import toast from "react-hot-toast";
import moment from "moment";
import { ROWS_PER_PAGE, SellStatusEnum } from "../../../actionTypes";
import { useTranslation } from "react-i18next";
import { Input } from "antd";
import { render } from "bwip-js";

const SellList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rowsPerPage = ROWS_PER_PAGE;
  const [search, setSearch] = useState("");
  const [anchorElMap, setAnchorElMap] = useState({});
  const [deleteStatus, setDeleteStatus] = useState(false);

  const handleClick = (event, rowData) => {
    setAnchorElMap((prevMap) => ({
      ...prevMap,
      [rowData.id]: event.currentTarget,
    }));
  };

  const handleClose = (rowData) => {
    setAnchorElMap((prevMap) => ({
      ...prevMap,
      [rowData.id]: null,
    }));
  };

  const renderActionsColumn = (rowData) => {
    return (
      <>
        <IconButton onClick={(event) => handleClick(event, rowData)}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorElMap[rowData.id]}
          open={Boolean(anchorElMap[rowData.id])}
          keepMounted
          onClose={() => handleClose(rowData)}
          sx={{
            boxShadow:
              "0px 7px 8px -4px rgb(0 0 0 / 20%), 0px 12px 17px 2px rgb(0 0 0 / 14%), 0px 5px 22px 4px rgb(0 0 0 / 12%)",
          }}
        >
          <MenuItem
            sx={{ fontSize: "14px" }}
            onClick={() => handleEditClick(rowData.id)}
          >
            {t("Edit")}
          </MenuItem>
          {rowData.delivery_status_id !=
            SellStatusEnum.Delivered.toString() && (
            <MenuItem
              sx={{ fontSize: "14px" }}
              onClick={() => handleDeleteClick(rowData.id)}
            >
              {t("Delete")}
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };
  const columns = [
    { columnName: "sales_no", columnShow: "Sell No" },
    {
      columnName: "serial_number",
      columnShow: "serial_number",
      render: (row) => {
        let productDetails = [];
        if (typeof row.product_details === "string") {
          try {
            productDetails = JSON.parse(row.product_details || "[]");
          } catch {
            productDetails = [];
          }
        } else if (Array.isArray(row.product_details)) {
          productDetails = row.product_details;
        } else {
          productDetails = [];
        }
        const serialNumber =
          productDetails &&
          productDetails.length > 0 &&
          productDetails[0].serial_number;
        return (
          <span className="text-left">
            {serialNumber ? serialNumber : "N/A"}
          </span>
        );
      },
    },
    { columnName: "sale_date", columnShow: "Sell Date" },
    {
      columnName: "product_name",
      columnShow: "product_name",
      render: (row) => {
        let productDetails = [];
        if (typeof row.product_details === "string") {
          try {
            productDetails = JSON.parse(row.product_details || "[]");
          } catch {
            productDetails = [];
          }
        } else if (Array.isArray(row.product_details)) {
          productDetails = row.product_details;
        } else {
          productDetails = [];
        }
        const productName =
          productDetails &&
          productDetails.length > 0 &&
          productDetails[0].product_name;
        return (
          <span className="text-left">{productName ? productName : "N/A"}</span>
        );
      },
    },
    {
      columnName: "weight",
      columnShow: "Weight",
      render: (row) => {
        let productDetails = [];
        if (typeof row.product_details === "string") {
          try {
            productDetails = JSON.parse(row.product_details || "[]");
          } catch {
            productDetails = [];
          }
        } else if (Array.isArray(row.product_details)) {
          productDetails = row.product_details;
        } else {
          productDetails = [];
        }
        const weight =
          productDetails &&
          productDetails.length > 0 &&
          productDetails[0].weight;
        return <span className="text-left">{weight ? weight : "N/A"}</span>;
      },
    },
    { columnName: "wirehouse_name", columnShow: "wirehouse_name" },
    { columnName: "customer_name", columnShow: "customer_name" },
    {
      columnName: "actions",
      columnShow: "Actions",
      render: renderActionsColumn,
    },
  ];

  const cellFormatting = {
    sales_no: {
      textAlign: "left",
    },
    sale_date: {
      textAlign: "left",
    },
    actions: {
      width: "100px",
      textAlign: "center",
    },
  };
  const manipulateData = (data) => {
    // Perform data manipulation here
    return data.map((item) => ({
      ...item,
      sale_date: moment(item.sale_date).format("DD-MMM-YYYY"),
    }));
  };

  const handleEditClick = (id) => {
    navigate(`/sell/${id}`, { replace: true });
  };

  const handleDeleteClick = async (id) => {
    const res = await dispatch(deleteSell(id))
      .then((response) => {
        if (
          response &&
          response.payload &&
          response.payload.statusCode === 200
        ) {
          toast.success(
            response && response.payload && response.payload.message
          );
          setDeleteStatus(true);
          //global?.window?.location.reload();
        }
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        toast.error(error);
      });
  };

  const debounceTimer = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0); // To trigger grid refresh

  // useEffect(() => {
  //   if (debounceTimer.current) clearTimeout(debounceTimer.current);
  //   debounceTimer.current = setTimeout(() => {
  //     setRefreshKey((k) => k + 1); // Triggers CommonDataGrid to refetch
  //   }, 1000);
  //   return () => clearTimeout(debounceTimer.current);
  // }, [search]);
  useEffect(() => {
    const handler = setTimeout(() => {
      setRefreshKey((prev) => prev + 1);
    }, 1000); // 1 second debounce

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const fetchData = async (page = 0, pageSize = rowsPerPage) => {
    const response = await dispatch(
      getSellsWithPagination({
        tableName: "sales_master",
        page: page - 1,
        pageSize: pageSize,
        search: search,
      })
    );

    const data = (await response.payload) && response.payload.data;
    return {
      data: (data && data.rows) || [],

      total: (data && data.total) || 0,
      totalPages: (data && Math.ceil(data.total / rowsPerPage)) || 0,
    };
  };
  //   const fetchData = useCallback(
  //   async (page = 0, pageSize = rowsPerPage) => {
  //     const response = await dispatch(
  //       getSellsWithPagination({
  //         tableName: "sales_master",
  //         page: page - 1,
  //         pageSize: pageSize,
  //         search: search,
  //       })
  //     );
  //     const data = (await response.payload) && response.payload.data;
  //     return {
  //       data: (data && data.rows) || [],
  //       total: (data && data.total.total) || 0,
  //       totalPages: (data && Math.ceil(data.total.total / rowsPerPage)) || 0,
  //     };
  //   },
  //   [dispatch, search, rowsPerPage]
  // );
  useEffect(() => {
    if (deleteStatus) {
      fetchData(1); // Fetch data again after deletion
      setDeleteStatus(false); // Reset delete status
    }
  }, [deleteStatus]);
  return (
    <div className="p-4">
      <div className="w-full flex items-center justify-between mb-6">
        <Typography variant="h6">{t("Sell List")}</Typography>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate("/sell/new", { replace: true })}
        >
          {t("Add Sell")}
        </Button>
      </div>
      <Input
        className="mb-6 max-w-sm"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search sell by product name..."
      />
      <CommonDataGrid
        columns={columns}
        fetchData={fetchData}
        cellFormatting={cellFormatting}
        dataManipulator={manipulateData}
        key={refreshKey}
      />
    </div>
  );
};

export default SellList;
